import { normalize } from '../../shared/handle/index.js';

export const formatDateTime = (value) => {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (err) {
        return value;
    }
};

export const formatRelativeTime = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
        return 'Сегодня';
    } else if (diffDays === 1) {
        return 'Вчера';
    } else if (diffDays < 7) {
        return `${diffDays} дн. назад`;
    } else {
        return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
    }
};

export const getInitials = (value = '') => {
    return value
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(part => part[0]?.toUpperCase() || '')
        .join('') || 'CL';
};

export const copyToClipboard = async (value) => {
    if (!value) return;
    try {
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(value);
        } else {
            const textarea = document.createElement('textarea');
            textarea.value = value;
            textarea.setAttribute('readonly', '');
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            textarea.style.pointerEvents = 'none';
            document.body.appendChild(textarea);
            textarea.focus({ preventScroll: true });
            textarea.select();
            document.execCommand('copy');
            document.body.removeChild(textarea);
        }
    } catch (err) {
        console.error('Не удалось скопировать в буфер обмена', err);
    }
};

export const normalizeTikTokValue = (input) => {
    if (!input || typeof input !== 'string') return null;
    let value = input.trim();
    if (!value) return null;
    if (/^https?:\/\//i.test(value)) {
        return value;
    }
    if (value.startsWith('www.')) {
        return `https://${value}`;
    }
    if (/tiktok\.com/i.test(value)) {
        const sanitized = value.replace(/^https?:\/\//i, '');
        return `https://${sanitized.replace(/^\/+/, '')}`;
    }
    if (value.startsWith('@')) {
        value = value.slice(1);
    }
    value = value.replace(/^tiktokuser:/i, '').trim();
    if (!value) return null;
    return `https://www.tiktok.com/@${value}`;
};

export const normalizeHandle = (value, platform = null) => normalize(value, platform);

export const extractTikTokCandidates = (metadata) => {
    if (!metadata || typeof metadata !== 'object') return [];
    const channels = metadata.channels || metadata.socials || {};
    const contacts = metadata.contacts || {};
    const links = metadata.links || {};
    return [
        metadata.tiktok,
        metadata.tikTok,
        metadata.tt,
        channels.tiktok,
        channels.tikTok,
        contacts.tiktok,
        contacts.tikTok,
        links.tiktok,
        links.tikTok
    ].filter(value => typeof value === 'string' && value.trim());
};

export const formatTikTokDisplay = (normalized, raw) => {
    if (raw && typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) {
            return '';
        }
        if (!/^https?:\/\//i.test(trimmed)) {
            return trimmed.startsWith('@') ? trimmed : `@${trimmed.replace(/^@/, '')}`;
        }
    }
    const match = normalized?.match(/tiktok\.com\/@([^/?]+)/i);
    if (match && match[1]) {
        return `@${match[1]}`;
    }
    return normalized || '';
};

export const presentTikTokInput = (value) => {
    if (!value || typeof value !== 'string') return '';
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) {
        const match = trimmed.match(/tiktok\.com\/@([^/?]+)/i);
        if (match && match[1]) {
            return `@${match[1]}`;
        }
        return trimmed;
    }
    if (trimmed.startsWith('@')) {
        return trimmed;
    }
    if (/tiktok\.com/i.test(trimmed)) {
        return trimmed.startsWith('http') ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`;
    }
    return `@${trimmed}`;
};

export const getLeadTikTokInfo = (lead) => {
    const candidates = [];
    if (typeof lead?.tiktok === 'string') {
        candidates.push(lead.tiktok);
    }
    candidates.push(...extractTikTokCandidates(lead?.metadata));
    for (const candidate of candidates) {
        const normalized = normalizeTikTokValue(candidate);
        if (normalized) {
            return {
                raw: candidate,
                url: normalized,
                display: formatTikTokDisplay(normalized, candidate),
                input: presentTikTokInput(candidate)
            };
        }
    }
    return { raw: '', url: null, display: '', input: '' };
};

export const buildLeadMetadata = (baseMetadata, updates = {}) => {
    const next = baseMetadata ? { ...baseMetadata } : {};
    if (Object.prototype.hasOwnProperty.call(updates, 'negotiationMin')) {
        const value = updates.negotiationMin;
        if (value !== undefined) {
            if (value !== null) {
                next.negotiationPriceMin = value;
            } else {
                delete next.negotiationPriceMin;
            }
            const negotiation = typeof next.negotiation === 'object' && next.negotiation !== null
                ? { ...next.negotiation }
                : {};
            if (value !== null && value !== undefined) {
                negotiation.min = value;
            } else {
                delete negotiation.min;
            }
            if (Object.keys(negotiation).length > 0) {
                next.negotiation = negotiation;
            } else {
                delete next.negotiation;
            }
        }
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'negotiationMax')) {
        const value = updates.negotiationMax;
        if (value !== undefined) {
            if (value !== null) {
                next.negotiationPriceMax = value;
            } else {
                delete next.negotiationPriceMax;
            }
            const negotiation = typeof next.negotiation === 'object' && next.negotiation !== null
                ? { ...next.negotiation }
                : {};
            if (value !== null && value !== undefined) {
                negotiation.max = value;
            } else {
                delete negotiation.max;
            }
            if (Object.keys(negotiation).length > 0) {
                next.negotiation = negotiation;
            } else {
                delete next.negotiation;
            }
        }
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'tiktok')) {
        const value = updates.tiktok;
        if (value) {
            next.tiktok = value;
        } else {
            delete next.tiktok;
        }
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'payment')) {
        const value = updates.payment;
        if (value && value.method && value.details) {
            next.payment = { method: value.method, details: value.details };
        } else {
            delete next.payment;
        }
    }
    if (Object.prototype.hasOwnProperty.call(updates, 'application')) {
        const value = updates.application;
        if (value && typeof value === 'string' && value.trim()) {
            next.application = value.trim();
        } else {
            delete next.application;
        }
    }
    return Object.keys(next).length > 0 ? next : null;
};

export const formatDuration = (seconds) => {
    const total = Math.max(0, Number.isFinite(seconds) ? Math.floor(seconds) : 0);
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    if (days > 0) {
        return `${days}д ${hours.toString().padStart(2, '0')}ч ${minutes.toString().padStart(2, '0')}м`;
    }
    if (hours > 0) {
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

export const computeRemainingSeconds = (timer) => {
    if (!timer || !Number.isFinite(timer.deadline)) return 0;
    const diffMs = timer.deadline - Date.now();
    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
};

export const DEFAULT_DELAY_RANGE = { minSeconds: 8, maxSeconds: 25 };

export const normalizeDelayRange = (range, fallback) => {
    const reference = fallback || DEFAULT_DELAY_RANGE;
    const minCandidate = Number(range?.minSeconds);
    const maxCandidate = Number(range?.maxSeconds);
    const minResolved = Number.isFinite(minCandidate) ? Math.max(0, minCandidate) : reference.minSeconds;
    const maxResolved = Number.isFinite(maxCandidate) ? Math.max(minResolved, maxCandidate) : reference.maxSeconds;
    return {
        minSeconds: minResolved,
        maxSeconds: maxResolved
    };
};

export const channelLabelMap = {
    instagram: 'Instagram',
    email: 'Email',
    whatsapp: 'WhatsApp',
    wazzup: 'Wazzup',
    telegram: 'Telegram',
    vk: 'VK'
};

export const formatChannel = (channel) => channelLabelMap[channel] || channel;

export const getChannelChipClass = (channel) => {
    const normalized = (channel || '').toLowerCase();
    return ['chip', normalized ? `chip--${normalized}` : ''].filter(Boolean).join(' ');
};

export function timeAgo(date) {
    if (!date) return '\u2014';
    const d = new Date(date);
    const now = new Date();
    const diffMs = now - d;
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (days === 0) return 'сегодня';
    if (days === 1) return 'вчера';
    if (days < 7) return `${days} дн назад`;
    if (days < 30) return `${Math.floor(days / 7)} нед назад`;
    if (days < 365) return `${Math.floor(days / 30)} мес назад`;
    return `${Math.floor(days / 365)} г назад`;
}
